import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SearchBox from '@/components/feature/SearchBox';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  allArticles,
  allCategories,
  allTags,
  parseTime,
  searchArticles,
  sortByHot,
  sortByTime,
} from '@/utils/news';

const REFERENCE_TIME = Math.max(...allArticles.map((item) => parseTime(item.publishTime)));

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [category, setCategory] = useState('all');
  const [timeValue, setTimeValue] = useState('all');

  usePageMeta(
    keyword ? `“${keyword}” 搜索结果 - 热搜榜` : '搜索 - 热搜榜',
    `在热搜榜中搜索“${keyword}”，查看社会、娱乐、科技等领域的相关热点资讯与趋势内容。`,
  );

  const results = useMemo(() => {
    let list = searchArticles(keyword);
    if (category !== 'all') list = list.filter((item) => item.category === category);
    if (timeValue !== 'all') {
      const day = 86400000;
      const span = timeValue === 'today' ? 1 : timeValue === '3days' ? 3 : 7;
      list = list.filter((item) => REFERENCE_TIME - parseTime(item.publishTime) < day * span);
    }
    return sortByTime(list);
  }, [keyword, category, timeValue]);

  const recommendations = sortByHot(allArticles).slice(0, 6);

  const chip = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
        : 'bg-background-200 text-foreground-600 hover:text-primary-600'
    }`;

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-page px-4 py-6 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? (
              <>
                搜索“<span className="text-primary-600">{keyword}</span>”
              </>
            ) : (
              '资讯搜索'
            )}
          </h1>
          <div className="mt-4 max-w-2xl">
            <SearchBox key={keyword} size="lg" defaultValue={keyword} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-foreground-500">分类</span>
          <button type="button" onClick={() => setCategory('all')} className={chip(category === 'all')}>
            全部
          </button>
          {allCategories.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => setCategory(cat.slug)}
              className={chip(category === cat.slug)}
            >
              {cat.name}
            </button>
          ))}

          <span className="ml-2 text-xs font-medium text-foreground-500">时间</span>
          <button type="button" onClick={() => setTimeValue('all')} className={chip(timeValue === 'all')}>
            全部
          </button>
          <button type="button" onClick={() => setTimeValue('today')} className={chip(timeValue === 'today')}>
            今日
          </button>
          <button type="button" onClick={() => setTimeValue('3days')} className={chip(timeValue === '3days')}>
            近三天
          </button>
          <button type="button" onClick={() => setTimeValue('7days')} className={chip(timeValue === '7days')}>
            近一周
          </button>
        </div>

        {!keyword ? (
          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center text-4xl text-foreground-300">
              <i className="ri-search-2-line"></i>
            </div>
            <p className="mt-3 text-sm text-foreground-500">请输入关键词开始搜索，例如“大模型”“音乐节”“城市更新”。</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {allTags.slice(0, 8).map((tag) => (
                <Link
                  key={tag.name}
                  to={`/search?q=${encodeURIComponent(tag.name)}`}
                  className="rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-900 transition-colors hover:bg-accent-500 hover:text-background-50"
                >
                  #{tag.name}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4 text-sm text-foreground-500">
              共找到 <strong className="text-primary-600">{results.length}</strong> 条与“{keyword}”相关的资讯
              <span className="ml-2 text-xs text-foreground-400">（前端演示数据）</span>
            </div>

            {results.length === 0 ? (
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center text-4xl text-foreground-300">
                  <i className="ri-file-search-line"></i>
                </div>
                <p className="mt-3 text-sm text-foreground-500">未找到与“{keyword}”相关的资讯，换个关键词试试。</p>
                <div className="mt-5">
                  <p className="mb-3 text-xs font-medium text-foreground-500">大家都在搜</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {allTags.slice(0, 8).map((tag) => (
                      <Link
                        key={tag.name}
                        to={`/search?q=${encodeURIComponent(tag.name)}`}
                        className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
                      >
                        #{tag.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mt-8 text-left">
                  <h2 className="mb-4 font-heading text-lg font-bold text-foreground-900">热门资讯推荐</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {recommendations.slice(0, 4).map((item) => (
                      <ArticleCard key={item.id} article={item} variant="grid" />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-4">
                {results.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}